import { yupResolver } from '@hookform/resolvers/yup'
import { getMessage } from '@limitless-minds/app-messages'
import { Button } from '@limitless-minds/ui-button'
import { Input } from '@limitless-minds/ui-input'
import { Stack } from '@mui/material'
import React, { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import { base as baseConfig } from '@/config'
import { useVerifyMFAcode } from '@/data/auth/hooks/useVerifyCode'
import { useStore } from '@/store'
import { Error as IError } from '@/types/Error'
import ErrorMessage from '@/ui/atoms/ErrorMessage'

import { FIELDS, schema } from './schema'

type FormValues = {
  code: string
}

interface MfaProps {
  goBackToLogin: React.Dispatch<React.SetStateAction<number>>
}
const MFAForm: React.FC<MfaProps> = ({ goBackToLogin }) => {
  const [failedLoginMessage, setFailedLoginMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { mutate: verifyMFA } = useVerifyMFAcode()

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      [FIELDS.CODE]: '',
    },
  })

  const onSubmit: SubmitHandler<FormValues> = (data: FormValues) => {
    const { code } = data
    setIsSubmitting(true)
    verifyMFA(
      { body: { code } },
      {
        onSuccess: () => {
          setFailedLoginMessage('')
          setIsSubmitting(false)

          // Clear the local storage of Zustand store
          useStore.persist.clearStorage()
          const url = sessionStorage.getItem('redirectUrl')
          sessionStorage.removeItem('redirectUrl')
          if (url) {
            window.location.href = `${url}`
          } else {
            window.location.href = `${baseConfig.webAppBaseUrl}`
          }
        },
        onError: (error) => {
          setIsSubmitting(false)
          const errorResponse = error as IError
          if (errorResponse.response?.status === 401) {
            goBackToLogin(0)
          }
          const errorMessage = getMessage(
            errorResponse.response?.data?.error?.errorCode,
            errorResponse.response?.data?.error?.params
          )
          setError(FIELDS.CODE, {
            type: 'manual',
            message: errorMessage,
          })
        },
      }
    )
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{ display: 'flex', flexDirection: 'column', gap: '2rem', width: '100%' }}
    >
      <Stack gap='0.75rem'>
        {failedLoginMessage !== '' && <ErrorMessage>{failedLoginMessage}</ErrorMessage>}
        <Controller
          name={FIELDS.CODE}
          control={control}
          render={({ field }) => (
            <Input
              label={'Verification Code'}
              {...field}
              error={!!errors?.[FIELDS.CODE]}
              errorMessage={errors?.[FIELDS.CODE]?.message}
            />
          )}
        />
      </Stack>
      <Button type='submit' width='100%' disabled={isSubmitting}>
        Submit
      </Button>
    </form>
  )
}

export default MFAForm
